import React from 'react';
import { 
    Box, 
    Container, 
    Typography, 
    Grid, 
    Card, 
    CardContent, 
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SecurityIcon from '@mui/icons-material/Security';
import InfoIcon from '@mui/icons-material/Info';
import SettingsIcon from '@mui/icons-material/Settings';
import LockIcon from '@mui/icons-material/Lock';
import ShareIcon from '@mui/icons-material/Share';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import UpdateIcon from '@mui/icons-material/Update';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import ShieldIcon from '@mui/icons-material/Shield';

// Styled components
const StyledHero = styled(Box)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    padding: theme.spacing(8, 0),
    textAlign: 'center',
    color: 'white',
}));

const StyledSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0),
}));

const PolicyCard = styled(Card)(({ theme }) => ({
    height: '100%',
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
    transition: 'transform 0.3s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    background: 'linear-gradient(45deg, #667eea, #764ba2)',
    color: 'white',
    margin: '0 auto',
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
    borderRadius: '12px !important',
    marginBottom: theme.spacing(2),
    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
    '&:before': {
        display: 'none',
    },
}));

export default function PrivacyPolicy() {
    const policyData = [
        {
            icon: <InfoIcon />,
            title: "Information We Collect",
            items: [
                "Personal Information: Name, email address, phone number, billing/shipping address.",
                "Payment Information: Credit/debit card details (processed securely).",
                "KYC Documents: PAN and Aadhar number for identity verification, KYC compliance, and TDS processing for any referral credits.",
                "Non-Personal Information: Browser type, IP address, and cookies to improve user experience."
            ]
        },
        {
            icon: <SettingsIcon />,
            title: "How We Use Your Information",
            items: [
                "To process and deliver your orders.",
                "To improve our products and services.",
                "To communicate updates, offers, and promotions."
            ]
        },
        {
            icon: <LockIcon />,
            title: "Data Security",
            items: [
                "We implement strict security measures to protect your personal data.",
                "Your information is securely stored and accessible only to authorized personnel."
            ]
        },
        {
            icon: <ShareIcon />,
            title: "Sharing Your Information",
            items: [
                "We do not sell or share your personal information with third parties.",
                "Information may be shared only with trusted partners involved in fulfilling your orders."
            ]
        },
        {
            icon: <AccountCircleIcon />,
            title: "Your Rights",
            items: [
                "You can request access, correction, or deletion of your personal information.",
                "Contact us at info@glamourradiance.com for any data-related requests."
            ]
        },
        {
            icon: <UpdateIcon />,
            title: "Changes to Privacy Policy",
            items: [
                "We may update this policy periodically.",
                "Any changes will be reflected on this page with the updated date."
            ]
        }
    ];

    return (
        <Box>
            {/* Hero Section */}
            <StyledHero>
                <Container maxWidth="lg">
                    <ShieldIcon sx={{ fontSize: '4rem', mb: 2, opacity: 0.9 }} />
                    <Typography 
                        variant="h2" 
                        component="h1" 
                        gutterBottom 
                        sx={{ 
                            fontWeight: 'bold',
                            textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                        }}
                    >
                        Privacy Policy
                    </Typography>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            maxWidth: '800px', 
                            mx: 'auto',
                            lineHeight: 1.6,
                            fontWeight: 300,
                            opacity: 0.95
                        }}
                    >
                        At Glamour & Radiance, we value your privacy and are committed to protecting 
                        your personal information. This Privacy Policy explains how we collect, use, 
                        and safeguard your data.
                    </Typography>
                </Container>
            </StyledHero>

            {/* Last Updated */}
            <StyledSection sx={{ py: 2, backgroundColor: 'grey.100' }}>
                <Container maxWidth="lg">
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            textAlign: 'center',
                            color: 'text.secondary',
                            fontStyle: 'italic'
                        }}
                    >
                        Last updated: September 30, 2025
                    </Typography>
                </Container>
            </StyledSection>

            {/* Policy Overview Cards */}
            <StyledSection>
                <Container maxWidth="lg">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 6
                        }}
                    >
                        Our Privacy Commitment
                    </Typography>
                    
                    <Grid container spacing={4}>
                        {policyData.map((section, index) => (
                            <Grid item xs={12} md={6} lg={4} key={index}>
                                <PolicyCard>
                                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                                        <IconWrapper>
                                            {section.icon}
                                        </IconWrapper>
                                        <Typography 
                                            variant="h6" 
                                            component="h3" 
                                            gutterBottom 
                                            sx={{ 
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                mb: 3
                                            }}
                                        >
                                            {section.title}
                                        </Typography>
                                        <List sx={{ textAlign: 'left' }}>
                                            {section.items.map((item, itemIndex) => (
                                                <ListItem key={itemIndex} sx={{ px: 0, py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: '24px' }}>
                                                        <Box 
                                                            sx={{ 
                                                                width: '8px', 
                                                                height: '8px', 
                                                                borderRadius: '50%', 
                                                                backgroundColor: 'primary.main' 
                                                            }} 
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={item} 
                                                        sx={{ 
                                                            '& .MuiListItemText-primary': { 
                                                                fontSize: '0.9rem',
                                                                lineHeight: 1.5,
                                                                color: 'text.primary'
                                                            } 
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                </PolicyCard>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </StyledSection>

            <Divider />

            {/* Detailed Accordion Section */}
            <StyledSection sx={{ backgroundColor: 'grey.50' }}>
                <Container maxWidth="md">
                    <Typography 
                        variant="h4" 
                        component="h2" 
                        gutterBottom 
                        sx={{ 
                            textAlign: 'center',
                            color: 'primary.main',
                            fontWeight: 600,
                            mb: 4
                        }}
                    >
                        Detailed Policy Information
                    </Typography>

                    {policyData.map((section, index) => (
                        <StyledAccordion key={index}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: 'primary.50',
                                    borderRadius: '12px',
                                    '&.Mui-expanded': {
                                        borderRadius: '12px 12px 0 0',
                                    }
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Box 
                                        sx={{ 
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '50%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: 'primary.main',
                                            color: 'white'
                                        }}
                                    >
                                        {section.icon}
                                    </Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {index + 1}. {section.title}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: 3 }}>
                                <List>
                                    {section.items.map((item, itemIndex) => (
                                        <ListItem key={itemIndex} sx={{ px: 0 }}>
                                            <ListItemIcon sx={{ minWidth: '24px' }}>
                                                <Box 
                                                    sx={{ 
                                                        width: '6px', 
                                                        height: '6px', 
                                                        borderRadius: '50%', 
                                                        backgroundColor: 'primary.main' 
                                                    }} 
                                                />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={item} 
                                                sx={{ 
                                                    '& .MuiListItemText-primary': { 
                                                        fontSize: '1rem',
                                                        lineHeight: 1.6,
                                                        color: 'text.primary'
                                                    } 
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </StyledAccordion>
                    ))}
                </Container>
            </StyledSection>

            {/* Security Assurance Section */}
            <StyledSection>
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <SecurityIcon 
                                    sx={{ 
                                        fontSize: '6rem',
                                        color: 'primary.main',
                                        mb: 2
                                    }} 
                                />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Typography 
                                variant="h4" 
                                component="h3" 
                                gutterBottom 
                                sx={{ 
                                    color: 'primary.main',
                                    fontWeight: 600
                                }}
                            >
                                Your Privacy is Our Priority
                            </Typography>
                            <Typography 
                                variant="body1" 
                                paragraph 
                                sx={{ 
                                    fontSize: '1.1rem',
                                    lineHeight: 1.7,
                                    color: 'text.primary'
                                }}
                            >
                                We use industry-standard encryption and security measures to protect your 
                                personal information. Our team is trained in data protection best practices, 
                                and we regularly review and update our security protocols.
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontSize: '1.1rem',
                                    lineHeight: 1.7,
                                    color: 'text.primary'
                                }}
                            >
                                Your trust is important to us. We are committed to transparency in how we 
                                handle your data and will always respect your privacy choices.
                            </Typography>
                        </Grid>
                    </Grid>
                </Container>
            </StyledSection>

            {/* Contact Section */}
            <StyledSection sx={{ backgroundColor: 'primary.50' }}>
                <Container maxWidth="md">
                    <Box 
                        sx={{ 
                            textAlign: 'center',
                            p: 4,
                            backgroundColor: 'white',
                            borderRadius: '20px',
                            boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
                        }}
                    >
                        <EmailIcon 
                            sx={{ 
                                fontSize: '3rem',
                                color: 'primary.main',
                                mb: 2
                            }} 
                        />
                        <Typography 
                            variant="h5" 
                            component="h3" 
                            gutterBottom 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            Questions About Your Privacy?
                        </Typography>
                        <Typography 
                            variant="body1" 
                            color="text.secondary" 
                            sx={{ 
                                fontSize: '1.1rem',
                                lineHeight: 1.6,
                                mb: 3
                            }}
                        >
                            If you have any questions about this Privacy Policy or how we handle your 
                            personal information, please don't hesitate to contact us.
                        </Typography>
                        <Typography 
                            variant="h6" 
                            sx={{ 
                                color: 'primary.main',
                                fontWeight: 600
                            }}
                        >
                            Email: info@glamourradiance.com
                        </Typography>
                    </Box>
                </Container>
            </StyledSection>
        </Box>
    );
}